import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Search, MapPin, Clock, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const FindCalendar = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCalendars();
  }, []);

  const fetchCalendars = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/public/calendars`);
      setCalendars(response.data.data || []);
    } catch (err) {
      console.error('Error fetching calendars:', err);
      setError('Failed to load calendars');
    } finally {
      setLoading(false);
    }
  };

  const filteredCalendars = calendars.filter(cal => 
    cal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cal.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cal.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectCalendar = (symbolicName) => {
    navigate(`/customer/login/${symbolicName}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <section className="py-20 bg-gradient-to-br from-white to-amber-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-light text-gray-800 mb-4">Find a Calendar</h1>
            <p className="text-xl text-gray-600">Search for businesses and book appointments online</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          <div className="mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by business name, type, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-lg border-2 border-gray-200 focus:border-yellow-500 focus:outline-none text-lg"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            {filteredCalendars.map((calendar) => (
              <div 
                key={calendar.id} 
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-semibold text-gray-800 mb-2">{calendar.name}</h3>
                    <div className="space-y-1">
                      <div className="flex items-center text-gray-600">
                        <Briefcase className="w-4 h-4 mr-2" />
                        <span>{calendar.type}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span>{calendar.location}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Clock className="w-4 h-4 mr-2" />
                        <span>{calendar.office_hours}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded">
                        {calendar.service_count} {calendar.service_count === 1 ? 'Service' : 'Services'} Available
                      </span>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleSelectCalendar(calendar.symbolic_name)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-3 rounded-md transition-colors whitespace-nowrap"
                  >
                    View Calendar
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {filteredCalendars.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                {searchTerm ? 'No calendars found. Try a different search term.' : 'No active calendars available at the moment.'}
              </p>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default FindCalendar;